import React, { useRef, useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { User, Lock, Bell, Shield, CreditCard, FileCheck, Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  authAPI,
  cardAPI,
  getErrorMessage,
  preferencesAPI,
} from '../../utils/api';
import { copyToClipboard } from '../../utils/helpers';
import { toast } from 'sonner';
import KYC from './KYC';

const SettingsNew = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    bio: '',
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
    sessionTimeout: '30',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    profileVisibility: true,
    activityStatus: true,
    loginAlerts: true,
  });
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [twoFADialog, setTwoFADialog] = useState({
    open: false,
    mode: 'enable',
    secret: '',
    otpauthUrl: '',
  });
  const [twoFACode, setTwoFACode] = useState('');
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [twoFAVerified, setTwoFAVerified] = useState(false);
  const [cards, setCards] = useState([]);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
  const [cardForm, setCardForm] = useState({
    cardholder_name: '',
    card_number: '',
    exp_month: '',
    exp_year: '',
    brand: 'Card',
    make_default: true,
  });
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    fetchUserData();
    fetchPreferences();
    fetchCards();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      setUser(userData);
      setProfileData({
        full_name: userData.full_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        bio: userData.bio || '',
      });
      setSecuritySettings((prev) => ({
        ...prev,
        twoFactorEnabled: Boolean(userData.is_2fa_enabled),
      }));
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await preferencesAPI.get();
      const prefs = response.data || {};
      setPreferences({
        emailNotifications: prefs.email_notifications ?? true,
        pushNotifications: prefs.push_notifications ?? false,
        smsNotifications: prefs.sms_notifications ?? false,
        profileVisibility: prefs.profile_visibility ?? true,
        activityStatus: prefs.activity_status ?? true,
        loginAlerts: prefs.login_alerts ?? true,
      });
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const fetchCards = async () => {
    try {
      setIsBillingLoading(true);
      const response = await cardAPI.getAll();
      setCards(response.data || []);
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true);
      const response = await authAPI.updateMe({
        full_name: profileData.full_name,
        phone: profileData.phone,
        bio: profileData.bio,
      });
      const updatedUser = response.data;
      setUser(updatedUser);
      setProfileData({
        full_name: updatedUser.full_name || '',
        email: updatedUser.email || profileData.email,
        phone: updatedUser.phone || '',
        bio: updatedUser.bio || '',
      });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordResetRequest = async () => {
    try {
      const email = profileData.email || user?.email;
      if (!email) {
        toast.error('No email found for this account');
        return;
      }
      await authAPI.requestPasswordReset({ email });
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset link'));
    }
  };

  const updatePreferences = async (updates) => {
    const previous = preferences;
    const next = { ...preferences, ...updates };
    setPreferences(next);
    try {
      setIsSavingPreferences(true);
      await preferencesAPI.update({
        email_notifications: next.emailNotifications,
        push_notifications: next.pushNotifications,
        sms_notifications: next.smsNotifications,
        profile_visibility: next.profileVisibility,
        activity_status: next.activityStatus,
        login_alerts: next.loginAlerts,
      });
    } catch (error) {
      setPreferences(previous);
      toast.error(getErrorMessage(error, 'Failed to update preferences'));
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const buildOtpauthUrl = (secret, email) => {
    const issuer = 'AquaBlockchain';
    const label = encodeURIComponent(`${issuer}:${email || 'user'}`);
    const params = new URLSearchParams({ secret, issuer });
    return `otpauth://totp/${label}?${params.toString()}`;
  };

  const startEnable2FA = async () => {
    try {
      setIs2FALoading(true);
      const response = await authAPI.enable2FA();
      const secret = response.data.secret;
      setTwoFAVerified(false);
      setTwoFADialog({
        open: true,
        mode: 'enable',
        secret,
        otpauthUrl: buildOtpauthUrl(secret, profileData.email || user?.email),
      });
      setTwoFACode('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start 2FA setup'));
    } finally {
      setIs2FALoading(false);
    }
  };

  const confirmEnable2FA = async () => {
    try {
      setIs2FALoading(true);
      await authAPI.verify2FA(twoFACode);
      setSecuritySettings((prev) => ({ ...prev, twoFactorEnabled: true }));
      setTwoFAVerified(true);
      toast.success('2FA enabled successfully');
      // Auto-close after the user sees the confirmation tick.
      setTimeout(() => {
        setTwoFADialog({ open: false, mode: 'enable', secret: '', otpauthUrl: '' });
        setTwoFACode('');
        setTwoFAVerified(false);
      }, 1500);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to enable 2FA'));
    } finally {
      setIs2FALoading(false);
    }
  };

  const startDisable2FA = () => {
    setTwoFAVerified(false);
    setTwoFADialog({ open: true, mode: 'disable', secret: '', otpauthUrl: '' });
    setTwoFACode('');
  };

  const confirmDisable2FA = async () => {
    try {
      setIs2FALoading(true);
      await authAPI.disable2FA(twoFACode);
      setSecuritySettings((prev) => ({ ...prev, twoFactorEnabled: false }));
      setTwoFADialog({ open: false, mode: 'disable', secret: '', otpauthUrl: '' });
      setTwoFACode('');
      toast.success('2FA disabled successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to disable 2FA'));
    } finally {
      setIs2FALoading(false);
    }
  };

  const handleCopySecret = async () => {
    const ok = await copyToClipboard(twoFADialog.secret);
    toast[ok ? 'success' : 'error'](ok ? 'Secret copied to clipboard' : 'Copy failed');
  };

  const handleAddCard = async () => {
    const cardholderName = cardForm.cardholder_name.trim();
    const rawNumber = cardForm.card_number.replace(/\s+/g, '');
    const digitsOnly = rawNumber.replace(/[^0-9]/g, '');
    const expMonth = Number(cardForm.exp_month);
    const expYearInput = cardForm.exp_year.trim();
    const expYearNumeric = Number(expYearInput);
    let expYear = expYearNumeric;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (!cardholderName) {
      toast.error('Cardholder name is required');
      return;
    }
    if (digitsOnly.length < 12 || digitsOnly.length > 19) {
      toast.error('Card number looks invalid');
      return;
    }
    if (!Number.isInteger(expMonth) || expMonth < 1 || expMonth > 12) {
      toast.error('Expiration month must be 01-12');
      return;
    }
    if (!/^\d{2}$|^\d{4}$/.test(expYearInput)) {
      toast.error('Expiration year must be YY or YYYY');
      return;
    }
    if (!Number.isInteger(expYearNumeric)) {
      toast.error('Expiration year is invalid');
      return;
    }
    if (/^\d{2}$/.test(expYearInput)) {
      expYear = 2000 + expYearNumeric;
    }
    if (!Number.isInteger(expYear) || expYear < currentYear) {
      toast.error('Expiration year is invalid');
      return;
    }
    if (expYear === currentYear && expMonth < currentMonth) {
      toast.error('Card is expired');
      return;
    }

    try {
      setIsSavingCard(true);
      await cardAPI.add({
        cardholder_name: cardholderName,
        card_number: digitsOnly,
        exp_month: expMonth,
        exp_year: expYear,
        brand: cardForm.brand,
        make_default: cardForm.make_default,
      });
      setCardForm({
        cardholder_name: '',
        card_number: '',
        exp_month: '',
        exp_year: '',
        brand: 'Card',
        make_default: true,
      });
      setIsCardDialogOpen(false);
      toast.success('Card added');
      fetchCards();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add card'));
    } finally {
      setIsSavingCard(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await cardAPI.delete(cardId);
      setCards((prev) => prev.filter((item) => item.id !== cardId));
      toast.success('Card removed');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove card'));
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    try {
      await cardAPI.setDefault(cardId);
      setCards((prev) =>
        prev.map((item) => ({ ...item, is_default: item.id === cardId }))
      );
      toast.success('Default card updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set default card'));
    }
  };

  const handleAvatarSelect = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      event.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Avatar must be 2MB or less');
      event.target.value = '';
      return;
    }

    try {
      setIsUploadingAvatar(true);
      const response = await authAPI.uploadAvatar(file);
      const avatarUrl = response.data?.avatar_url;
      if (avatarUrl) {
        setUser((prev) => ({ ...prev, avatar_url: avatarUrl }));
      }
      toast.success('Avatar updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload avatar'));
    } finally {
      setIsUploadingAvatar(false);
      event.target.value = '';
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'kyc', name: 'Identity Verification', icon: FileCheck },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy', icon: Shield },
    { id: 'billing', name: 'Billing', icon: CreditCard },
  ];

  const defaultCard =
    cards.find((card) => card.is_default) || cards[0] || null;

  const avatarSrc = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`
    : null;

  return (
    <div className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
          Manage your account preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <Card className="p-4 h-fit">
          <nav className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex min-w-[180px] items-center space-x-3 px-3 py-2 rounded-lg transition-colors lg:min-w-0 lg:w-full ${activeTab === tab.id
                    ? 'bg-[#062136] text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium whitespace-nowrap">{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </Card>

        {/* Settings Content */}
        <Card className="lg:col-span-3 p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Profile Information
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Update your personal information
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#21bcfe] flex items-center justify-center overflow-hidden">
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt="User avatar"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-2xl sm:text-3xl font-bold">
                      {profileData.full_name?.charAt(0) || 'A'}
                    </span>
                  )}
                </div>
                <div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={handleAvatarSelect}
                    disabled={isUploadingAvatar}
                  >
                    {isUploadingAvatar ? 'Uploading...' : 'Change Avatar'}
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    JPG, PNG or GIF. Max 2MB
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={profileData.full_name}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        full_name: e.target.value,
                      })
                    }
                    className="mt-1 border-gray-200 dark:border-gray-700 bg-gray-50"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    onChange={(e) =>
                      setProfileData({ ...profileData, email: e.target.value })
                    }
                    className="mt-1 border-gray-200 dark:border-gray-700 bg-gray-50"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phone: e.target.value })
                    }
                    className="mt-1 border-gray-200 dark:border-gray-700 bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={profileData.bio}
                  onChange={(e) =>
                    setProfileData({ ...profileData, bio: e.target.value })
                  }
                  placeholder="Tell us about yourself..."
                  className="mt-1 max-h-40 resize-y border-gray-200 dark:border-gray-700"
                  rows={4}
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={fetchUserData}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="bg-[#062136] hover:bg-[#062136]/90 text-white"
                >
                  {isSavingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'kyc' && (
            <div className="space-y-6">
              <KYC />
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Security Settings
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Manage your account security
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Two-Factor Authentication
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Add an extra layer of security
                    </p>
                  </div>
                  <Switch
                    checked={securitySettings.twoFactorEnabled}
                    onCheckedChange={(checked) =>
                      checked ? startEnable2FA() : startDisable2FA()
                    }
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Login Alerts
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Get notified of new logins
                    </p>
                  </div>
                  <Switch
                    checked={preferences.loginAlerts}
                    onCheckedChange={(checked) =>
                      updatePreferences({ loginAlerts: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                For security, password changes are handled by email. We will send a
                reset link to your registered email address.
              </div>

              <Button
                onClick={handlePasswordResetRequest}
                className="bg-[#062136] hover:bg-[#062136]/90 text-white"
              >
                Send Password Reset Link
              </Button>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Notification Preferences
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Choose what notifications you receive
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Email Notifications
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Receive email updates
                    </p>
                  </div>
                  <Switch
                    checked={preferences.emailNotifications}
                    onCheckedChange={(checked) =>
                      updatePreferences({ emailNotifications: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Push Notifications
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Browser notifications
                    </p>
                  </div>
                  <Switch
                    checked={preferences.pushNotifications}
                    onCheckedChange={(checked) =>
                      updatePreferences({ pushNotifications: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      SMS Notifications
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Text message alerts
                    </p>
                  </div>
                  <Switch
                    checked={preferences.smsNotifications}
                    onCheckedChange={(checked) =>
                      updatePreferences({ smsNotifications: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Privacy Settings
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Control your privacy preferences
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Profile Visibility
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Who can see your profile
                    </p>
                  </div>
                  <Switch
                    checked={preferences.profileVisibility}
                    onCheckedChange={(checked) =>
                      updatePreferences({ profileVisibility: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Activity Status
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Show when you're online
                    </p>
                  </div>
                  <Switch
                    checked={preferences.activityStatus}
                    onCheckedChange={(checked) =>
                      updatePreferences({ activityStatus: checked })
                    }
                    disabled={isSavingPreferences}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Billing Information
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Manage your payment methods
                </p>
              </div>

              <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                {defaultCard ? (
                  <>
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <p className="text-sm opacity-80">Default Card</p>
                        <p className="text-2xl font-bold mt-1">
                          {defaultCard.brand}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-80">Expires</p>
                        <p className="font-semibold">
                          {String(defaultCard.exp_month).padStart(2, '0')}/
                          {defaultCard.exp_year}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm opacity-80">Card ending in</p>
                    <p className="font-mono text-lg">•••• •••• •••• {defaultCard.last4}</p>
                    <p className="text-sm opacity-80 mt-2">
                      {defaultCard.cardholder_name}
                    </p>
                  </>
                ) : (
                  <div>
                    <p className="text-sm opacity-80">No card on file</p>
                    <p className="text-2xl font-bold mt-2">Add a card</p>
                  </div>
                )}
              </Card>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Cards
                  </h3>
                  <Button variant="outline" onClick={() => setIsCardDialogOpen(true)}>
                    Add Card
                  </Button>
                </div>
                {isBillingLoading ? (
                  <p className="text-sm text-gray-500">Loading cards...</p>
                ) : cards.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No cards added yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {cards.map((card) => (
                      <div
                        key={card.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {card.brand} •••• {card.last4}
                          </p>
                          <p className="text-sm text-gray-500">
                            {card.cardholder_name} • Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {card.is_default ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              Default
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => handleSetDefaultCard(card.id)}
                            >
                              Set Default
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            onClick={() => handleDeleteCard(card.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={twoFADialog.open} onOpenChange={(open) => setTwoFADialog((prev) => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {twoFADialog.mode === 'enable' ? 'Enable 2FA' : 'Disable 2FA'}
            </DialogTitle>
            <DialogDescription>
              {twoFADialog.mode === 'enable'
                ? 'Scan the QR code or enter the secret in your authenticator app, then enter the 6-digit code.'
                : 'Enter the 6-digit code from your authenticator app to disable 2FA.'}
            </DialogDescription>
          </DialogHeader>

          {twoFADialog.mode === 'enable' && twoFADialog.otpauthUrl ? (
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-lg bg-white p-3">
                <QRCodeSVG value={twoFADialog.otpauthUrl} size={200} includeMargin={false} />
              </div>
              <div className="w-full">
                <Label className="text-xs text-gray-500">Manual entry code</Label>
                <div className="mt-1 flex items-center gap-2 rounded-md border bg-gray-50 p-2 dark:bg-slate-900">
                  <code className="flex-1 truncate font-mono text-xs">
                    {twoFADialog.secret}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleCopySecret}
                    aria-label="Copy secret"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div>
            <Label htmlFor="twofa_code">Authenticator Code</Label>
            <Input
              id="twofa_code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={twoFACode}
              onChange={(e) =>
                setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              className="mt-1"
              placeholder="123456"
            />
            {twoFAVerified ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                <Check className="h-4 w-4" />
                <span>Verified — 2FA is now enabled</span>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTwoFADialog((prev) => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button
              onClick={twoFADialog.mode === 'enable' ? confirmEnable2FA : confirmDisable2FA}
              disabled={is2FALoading || !twoFACode}
              className="bg-[#062136] hover:bg-[#062136]/90 text-white"
            >
              {is2FALoading
                ? 'Working...'
                : twoFADialog.mode === 'enable'
                ? 'Enable 2FA'
                : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCardDialogOpen} onOpenChange={setIsCardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Card</DialogTitle>
            <DialogDescription>
              Add a card to use as a payment method.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cardholder_name">Cardholder Name</Label>
              <Input
                id="cardholder_name"
                value={cardForm.cardholder_name}
                onChange={(e) =>
                  setCardForm({ ...cardForm, cardholder_name: e.target.value })
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="card_number">Card Number</Label>
              <Input
                id="card_number"
                value={cardForm.card_number}
                onChange={(e) => {
                  const value = e.target.value;
                  let brand = '';
                  // Card brand detection logic
                  if (/^4/.test(value)) brand = 'Visa';
                  else if (/^5[1-5]/.test(value)) brand = 'MasterCard';
                  else if (/^3[47]/.test(value)) brand = 'American Express';
                  else if (/^6(?:011|5)/.test(value)) brand = 'Discover';
                  else if (/^35/.test(value)) brand = 'JCB';
                  else if (/^3(?:0[0-5]|[68])/.test(value)) brand = 'Diners Club';
                  setCardForm({ ...cardForm, card_number: value, brand });
                }}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp_month">Exp Month</Label>
                <Input
                  id="exp_month"
                  value={cardForm.exp_month}
                  onChange={(e) =>
                    setCardForm({ ...cardForm, exp_month: e.target.value })
                  }
                  className="mt-1"
                  placeholder="MM"
                />
              </div>
              <div>
                <Label htmlFor="exp_year">Exp Year</Label>
                <Input
                  id="exp_year"
                  value={cardForm.exp_year}
                  onChange={(e) =>
                    setCardForm({ ...cardForm, exp_year: e.target.value })
                  }
                  className="mt-1"
                  placeholder="YY or YYYY"
                />
              </div>
            </div>
            {/* Card Brand auto-detect badge */}
            {cardForm.brand && (
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-700 border border-gray-200">
                  {cardForm.brand}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
              <span className="text-sm text-gray-700">Make default</span>
              <Switch
                checked={cardForm.make_default}
                onCheckedChange={(checked) =>
                  setCardForm({ ...cardForm, make_default: checked })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCardDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCard}
              disabled={
                isSavingCard ||
                !cardForm.cardholder_name.trim() ||
                !cardForm.card_number.trim() ||
                !cardForm.exp_month.trim() ||
                !cardForm.exp_year.trim()
              }
              className="bg-[#062136] hover:bg-[#062136]/90 text-white"
            >
              {isSavingCard ? 'Saving...' : 'Save Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsNew;
